import { useLogger } from "@bindery/logging/react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bindery/ui/components/ui/alert-dialog";
import { Button } from "@bindery/ui/components/ui/button";
import { useState } from "react";
import {
  browserResetDeps,
  type ResetProgressDeps,
  resetReaderProgress,
} from "./reset-progress";

export function ResetProgressDialog({
  open,
  onOpenChange,
  readerId,
  deps = browserResetDeps,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readerId: string;
  deps?: ResetProgressDeps;
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const [resetting, setResetting] = useState(false);
  const [problem, setProblem] = useState<"unsent" | "failed" | null>(null);
  const log = useLogger();

  const confirm = () => {
    if (resetting) return;
    setResetting(true);
    setProblem(null);
    resetReaderProgress(readerId, deps).then(
      (outcome) => {
        log.event("reading.progress.reset", {
          trigger: "manual_reset_dialog",
          outcome,
        });
        if (outcome === "unsent") {
          setProblem("unsent");
          setResetting(false);
        }
      },
      (error: unknown) => {
        log.error("reading.progress.reset.error", error);
        setProblem("failed");
        setResetting(false);
      },
    );
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => !resetting && onOpenChange(next)}
    >
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset all progress?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              This will clear all your reading history, bookmarks, and word
              progress from this device. This cannot be undone.
            </span>
            <span className="text-muted-foreground/80 block text-xs">
              If you&apos;ve signed in before, your saved progress will still be
              safe in your account.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {problem === "unsent" && (
          <p role="alert" className="text-sm">
            Some of your answers haven&apos;t been sent yet. Nothing was reset.
            Try again when you&apos;re online.
          </p>
        )}
        {problem === "failed" && (
          <p role="alert" className="text-sm">
            The reset didn&apos;t finish. Please try again.
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={resetting}>
            Keep my progress
          </AlertDialogCancel>
          <Button variant="destructive" onClick={confirm} disabled={resetting}>
            {resetting ? "One moment..." : "Reset everything"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
